import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, AlertCircle } from 'lucide-react';
import { useBrand } from '../../contexts/BrandContext';
import { forecastService, MonthlyForecast, ForecastScenario } from '../../lib/forecastService';
import { useToast } from '../../contexts/ToastContext';

interface ProfitLossForecastProps {
  monthsAhead: number;
  selectedBrandIds?: string[];
  selectedScenario?: ForecastScenario | null;
}

const ProfitLossForecast: React.FC<ProfitLossForecastProps> = ({ monthsAhead, selectedBrandIds, selectedScenario }) => {
  const { currentBrand, brands } = useBrand();
  const { showToast } = useToast();
  const [forecasts, setForecasts] = useState<MonthlyForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [localBrandIds, setLocalBrandIds] = useState<string[]>([]);
  const [showBrandSelector, setShowBrandSelector] = useState(false);

  useEffect(() => {
    if (selectedBrandIds && selectedBrandIds.length > 0) {
      setLocalBrandIds(selectedBrandIds);
    } else if (currentBrand) {
      setLocalBrandIds([currentBrand.id]);
    }
  }, [currentBrand, selectedBrandIds]);

  useEffect(() => {
    if (localBrandIds.length > 0) {
      loadForecast();
    }
  }, [localBrandIds, monthsAhead, selectedScenario]);

  const loadForecast = async () => {
    if (localBrandIds.length === 0) return;

    try {
      setLoading(true);
      if (localBrandIds.length === 1) {
        const data = await forecastService.generateProfitLossForecastWithScenario(
          localBrandIds[0],
          selectedScenario?.id || null,
          monthsAhead
        );
        setForecasts(data);
      } else {
        const scenarioIds = localBrandIds.map(() => selectedScenario?.id || null);
        const data = await forecastService.generateConsolidatedProfitLossWithScenario(
          localBrandIds,
          scenarioIds,
          monthsAhead
        );
        setForecasts(data);
      }
    } catch (error) {
      showToast('Failed to load profit/loss forecast', 'error');
      console.error('Error loading forecast:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBrandSelection = (brandId: string) => {
    if (localBrandIds.includes(brandId)) {
      if (localBrandIds.length > 1) {
        setLocalBrandIds(localBrandIds.filter(id => id !== brandId));
      }
    } else {
      if (localBrandIds.length < 3) {
        setLocalBrandIds([...localBrandIds, brandId]);
      }
    }
  };

  const getSelectedBrandsLabel = () => {
    if (localBrandIds.length === 1) {
      const brand = brands.find(b => b.id === localBrandIds[0]);
      return brand?.name || 'Select Brands';
    }
    return `${localBrandIds.length} Brands Combined`;
  };

  const hasChurnAdjustment = selectedScenario && selectedScenario.churn_rate_monthly > 0;
  const totalRevenue = forecasts.reduce((sum, f) =>
    sum + (hasChurnAdjustment && f.churn_adjusted_total_revenue ? f.churn_adjusted_total_revenue : f.total_revenue), 0
  );
  const totalExpenses = forecasts.reduce((sum, f) => sum + (f.total_expenses || 0), 0);
  const totalProfit = forecasts.reduce((sum, f) => sum + (f.net_profit || 0), 0);
  const avgMonthlyProfit = forecasts.length > 0 ? totalProfit / forecasts.length : 0;
  const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatMonth = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading profit/loss forecast...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!selectedBrandIds && (
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Compare Brands</h3>
            <p className="text-xs text-gray-600 mt-1">
              {localBrandIds.length === 1 ? 'Select additional brands to compare' : `Viewing ${localBrandIds.length} brands combined`}
            </p>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowBrandSelector(!showBrandSelector)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm font-medium text-gray-900">{getSelectedBrandsLabel()}</span>
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showBrandSelector && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <div className="p-3 border-b border-gray-200">
                  <p className="text-xs font-medium text-gray-700">Select Brands to Compare</p>
                  <p className="text-xs text-gray-500 mt-1">Choose 1-3 brands to view</p>
                </div>
                <div className="p-2 space-y-1">
                  {brands.map(brand => (
                    <button
                      key={brand.id}
                      onClick={() => toggleBrandSelection(brand.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                        localBrandIds.includes(brand.id)
                          ? 'bg-blue-50 text-blue-900'
                          : 'hover:bg-gray-50 text-gray-900'
                      }`}
                    >
                      <span className="text-sm font-medium">{brand.name}</span>
                      {localBrandIds.includes(brand.id) && (
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
                <div className="p-2 border-t border-gray-200">
                  <button
                    onClick={() => setShowBrandSelector(false)}
                    className="w-full px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                ${formatCurrency(totalRevenue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Expenses</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                ${formatCurrency(totalExpenses)}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Net Profit</p>
              <p className={`text-2xl font-bold mt-1 ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${formatCurrency(totalProfit)}
              </p>
            </div>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${totalProfit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              {totalProfit >= 0 ? (
                <TrendingUp className={`w-6 h-6 ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              ) : (
                <TrendingDown className="w-6 h-6 text-red-600" />
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Profit Margin</p>
              <p className={`text-2xl font-bold mt-1 ${profitMargin >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                {profitMargin.toFixed(1)}%
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Monthly Profit & Loss</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Month</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {hasChurnAdjustment ? 'Revenue (Churn Adj.)' : 'Revenue'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">One-Time Expenses</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Recurring Expenses</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Total Expenses</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Net Profit</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Margin</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {forecasts.map((forecast, index) => {
                const isCurrentMonth = index === 0;
                const margin = forecast.total_revenue > 0 ? ((forecast.net_profit || 0) / forecast.total_revenue) * 100 : 0;

                return (
                  <tr key={forecast.month} className={`hover:bg-gray-50 transition-colors ${isCurrentMonth ? 'bg-blue-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{formatMonth(forecast.month)}</span>
                        {isCurrentMonth && (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">Current</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex flex-col items-end">
                        <span className="text-sm text-green-600 font-medium">
                          ${formatCurrency(hasChurnAdjustment && forecast.churn_adjusted_total_revenue ? forecast.churn_adjusted_total_revenue : forecast.total_revenue)}
                        </span>
                        {hasChurnAdjustment && forecast.revenue_lost_to_churn && forecast.revenue_lost_to_churn > 0 && (
                          <span className="text-xs text-red-500">
                            (-${formatCurrency(forecast.revenue_lost_to_churn)})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      ${formatCurrency(forecast.one_time_expenses || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      ${formatCurrency(forecast.recurring_expenses || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-red-600 font-medium">
                      ${formatCurrency(forecast.total_expenses || 0)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-bold ${
                      (forecast.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ${formatCurrency(forecast.net_profit || 0)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-medium ${
                      margin >= 0 ? 'text-gray-900' : 'text-red-600'
                    }`}>
                      {margin.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr>
                <td className="px-6 py-4 text-sm font-bold text-gray-900">Total</td>
                <td className="px-6 py-4 text-right text-sm font-bold text-green-600">
                  ${formatCurrency(totalRevenue)}
                </td>
                <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                  ${formatCurrency(forecasts.reduce((sum, f) => sum + (f.one_time_expenses || 0), 0))}
                </td>
                <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                  ${formatCurrency(forecasts.reduce((sum, f) => sum + (f.recurring_expenses || 0), 0))}
                </td>
                <td className="px-6 py-4 text-right text-sm font-bold text-red-600">
                  ${formatCurrency(totalExpenses)}
                </td>
                <td className={`px-6 py-4 text-right text-sm font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${formatCurrency(totalProfit)}
                </td>
                <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                  {profitMargin.toFixed(1)}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProfitLossForecast;